import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Tooltip,
  TablePagination,
  Snackbar,
  Card,
  Chip,
  Stack,
  Container,
  Slide,
  alpha,
  Grid,
  capitalize
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Notifications as NotificationsIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import {
  ShoppingCart as OrderIcon,
  // Search as SearchIcon,
  // Receipt as ReceiptIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { Notification } from '../../types/notification';
import { notificationService } from '../../services/api';

const NotificationList: React.FC = () => {
  const { t } = useTranslation();
  const [ notifications, setNotifications ] = useState<Notification[]>( [] );
  const [ loading, setLoading ] = useState( false );
  const [ toast, setToast ] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' }>( {
    open: false,
    message: '',
    severity: 'success'
  } );
  const [ createDialogOpen, setCreateDialogOpen ] = useState( false );
  const [ deleteDialogOpen, setDeleteDialogOpen ] = useState( false );
  const [ resendDialogOpen, setResendDialogOpen ] = useState( false );
  const [ selectedNotification, setSelectedNotification ] = useState<Notification | null>( null );
  const [ page, setPage ] = useState( 0 );
  const [ rowsPerPage, setRowsPerPage ] = useState( 10 );
  const [ newNotification, setNewNotification ] = useState( {
    title: '',
    message: ''
  } );

  useEffect( () => {
    fetchNotifications();
  }, [] );

  const fetchNotifications = async () => {
    try {
      setLoading( true );
      const response = await notificationService.getNotifications();
      if ( response.success ) {
        setNotifications( response.data );
      } else {
        setToast( {
          open: true,
          message: response.message || t( 'notifications.error.fetch' ),
          severity: 'error'
        } );
      }
    } catch ( error ) {
      console.error( 'Error fetching notifications:', error );
      setToast( {
        open: true,
        message: t( 'notifications.error.fetch' ),
        severity: 'error'
      } );
    } finally {
      setLoading( false );
    }
  };

  const handleChangePage = ( event: unknown, newPage: number ) => {
    setPage( newPage );
  };

  const handleChangeRowsPerPage = ( event: React.ChangeEvent<HTMLInputElement> ) => {
    setRowsPerPage( parseInt( event.target.value, 10 ) );
    setPage( 0 );
  };
  const handleCloseCreateDialog = () => {
    setCreateDialogOpen( false );
    setNewNotification( { title: "", message: "" } ); // reset form
  };

  const handleCreateNotification = async () => {
    if ( !newNotification.title.trim() || !newNotification.message.trim() ) {
      setToast( {
        open: true,
        message: 'Please fill in all required fields',
        severity: 'warning'
      } );
      return;
    }

    try {
      setLoading( true );
      const response = await notificationService.createNotification( newNotification );
      if ( response.success ) {
        setCreateDialogOpen( false );
        setNewNotification( { title: '', message: '' } );
        fetchNotifications();
        setToast( {
          open: true,
          message: t( 'notifications.success.create' ),
          severity: 'success'
        } );
      } else {
        setToast( {
          open: true,
          message: response.message || t( 'notifications.error.create' ),
          severity: 'error'
        } );
      }
    } catch ( error ) {
      console.error( 'Error creating notification:', error );
      setToast( {
        open: true,
        message: t( 'notifications.error.create' ),
        severity: 'error'
      } );
    } finally {
      setLoading( false );
    }
  };

  const handleDeleteNotification = async ( id: string ) => {
    try {
      setLoading( true );
      const response = await notificationService.deleteNotification( id );
      if ( response.success ) {
        setDeleteDialogOpen( false );
        setSelectedNotification( null );
        fetchNotifications();
        setToast( {
          open: true,
          message: t( 'notifications.delete' ),
          severity: 'success'
        } );
      } else {
        setToast( {
          open: true,
          message: response.message || t( 'notifications.error.delete' ),
          severity: 'error'
        } );
      }
    } catch ( error ) {
      console.error( 'Error deleting notification:', error );
      setToast( {
        open: true,
        message: t( 'notifications.error.delete' ),
        severity: 'error'
      } );
    } finally {
      setLoading( false );
    }
  };

  const handleResendNotification = async ( id: string ) => {
    try {
      setLoading( true );
      const response = await notificationService.resendNotification( id );
      if ( response.success ) {
        setResendDialogOpen( false );
        setSelectedNotification( null );
        setToast( {
          open: true,
          message: t( 'notifications.resend' ),
          severity: 'success'
        } );
      } else {
        setToast( {
          open: true,
          message: response.message || t( 'notifications.error.resend' ),
          severity: 'error'
        } );
      }
    } catch ( error ) {
      console.error( 'Error resending notification:', error );
      setToast( {
        open: true,
        message: t( 'notifications.error.resend' ),
        severity: 'error'
      } );
    } finally {
      setLoading( false );
    }
  };

  const handleCloseToast = () => {
    setToast( { ...toast, open: false } );
  };

  const paginatedNotifications = notifications.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if ( loading && notifications.length === 0 ) {
    return (
      <Container maxWidth="lg" sx={ { py: 4 } }>
        <Box sx={ {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          gap: 2
        } }>
          <CircularProgress size={ 60 } />
          <Typography variant="h6" color="text.secondary">
            Loading notifications...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth={ false } sx={ { py: 4, width: '100%' } }>
      <Slide direction="down" in={ true } timeout={ 600 }>
        <Box>
          {/* Header Section */ }
          <Box sx={ { mb: 4 } }>
            <Stack direction="row" alignItems="center" spacing={ 2 } sx={ { mb: 2 } }>
              <Box sx={ {
                p: 1.5,
                borderRadius: 2,
                bgcolor: 'primary.main',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              } }>
                <NotificationsIcon sx={ { fontSize: 28 } } />
              </Box>
              <Box>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  { t( 'notifications.title' ) }
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Manage system notifications and announcements
                </Typography>
              </Box>
            </Stack>

            <Box sx={ { display: 'flex', justifyContent: 'space-between', alignItems: 'center' } }>
              <Chip
                icon={ <InfoIcon /> }
                label={ `${ notifications.length } notifications` }
                color="primary"
                variant="outlined"
              />
              <Button
                variant="contained"
                color="primary"
                startIcon={ <AddIcon /> }
                onClick={ () => setCreateDialogOpen( true ) }
                sx={ {
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)'
                  }
                } }
              >
                { t( 'notifications.create' ) }
              </Button>
            </Box>
          </Box>

          {/* Notifications Table */ }
          <Paper
            elevation={ 0 }
            sx={ {
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden'
            } }
          >
            { paginatedNotifications.length === 0 ? (

              <Box sx={ {
                textAlign: 'center',
                py: 8,
                color: 'text.secondary'
              } }>
                <OrderIcon sx={ { fontSize: 64, mb: 2, opacity: 0.5 } } />
                <Typography variant="h6" sx={ { mb: 1 } }>
                  No orders found
                </Typography>
                <Typography variant="body2">
                  { createDialogOpen ? 'Try adjusting your search criteria' : 'No orders available' }
                </Typography>
              </Box>

            ) : ( <>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={ {
                      backgroundColor: 'primary.main',
                      '& th': {
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }
                    } }>
                      <TableCell align="center" sx={ { width: '80px' } }>
                        S.NO
                      </TableCell>
                      <TableCell align="left">
                        { t( 'notifications.notificationTitle' ) }
                      </TableCell>
                      <TableCell >
                        { t( 'notifications.message' ) }
                      </TableCell>
                      <TableCell align="center" sx={ { width: '180px' } }>
                        { t( 'notifications.lastSentDate' ) }
                      </TableCell>
                      <TableCell align="center" sx={ { width: '150px' } }>
                        { t( 'notifications.actions' ) }
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    { paginatedNotifications.map( ( notification, idx ) => (
                      <TableRow
                        key={ notification.id }
                        sx={ {
                          '&:hover': {
                            backgroundColor: ( theme ) => alpha( theme.palette.primary.main, 0.04 )
                          },
                          transition: 'background-color 0.2s ease'
                        } }
                      >
                        <TableCell align="center" >
                          { page * rowsPerPage + idx + 1 }
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" >
                            { capitalize(notification.title )}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            <ExpandableText text={ capitalize(notification.message )} limit={ 80 } />
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" color="text.secondary">
                            { format( new Date( notification.updatedAt ), 'MMM dd, yyyy HH:mm' ) }
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={ 1 } justifyContent="center">
                            <Tooltip title="Resend notification">
                              <IconButton
                                onClick={ () => {
                                  setSelectedNotification( notification );
                                  setResendDialogOpen( true );
                                } }
                                size="small"
                                sx={ {
                                  color: 'info.main',
                                  '&:hover': {
                                    backgroundColor: ( theme ) => alpha( theme.palette.info.main, 0.1 )
                                  }
                                } }
                              >
                                <RefreshIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete notification">
                              <IconButton
                                onClick={ () => {
                                  setSelectedNotification( notification );
                                  setDeleteDialogOpen( true );
                                } }
                                size="small"
                                sx={ {
                                  color: 'error.main',
                                  '&:hover': {
                                    backgroundColor: ( theme ) => alpha( theme.palette.error.main, 0.1 )
                                  }
                                } }
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ) ) }
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={ [ 5, 10, 25 ] }
                component="div"
                count={ notifications.length }
                rowsPerPage={ rowsPerPage }
                page={ page }
                onPageChange={ handleChangePage }
                onRowsPerPageChange={ handleChangeRowsPerPage }
                sx={ {
                  borderTop: '1px solid',
                  borderColor: 'divider'
                } }
              /></> ) }

          </Paper>
        </Box>
      </Slide>

      {/* Create Notification Dialog */ }
      <Dialog
        open={ createDialogOpen }
        onClose={ handleCloseCreateDialog }
        maxWidth="sm"
        fullWidth
      // PaperProps={{
      //   sx: {
      //     borderRadius: 3,
      //     boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      //   }

      // }}
      >
        <DialogTitle sx={ {
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          fontWeight: 'bold',
          fontSize: '1.3rem',
          textAlign: 'center',
          gap: 2,
          py: 2.5,
          px: 3
        } }>
          { t( 'notifications.create' ) }
        </DialogTitle>
        <DialogContent sx={ { p: 4 } }>
          <Box sx={ { mb: 3 } }>
            {/* <Typography variant="h6" sx={{
              mb: 2,
              color: 'text.primary',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
            </Typography> */}
          </Box>
          <Grid container spacing={ 3 }>
            {/* Title Field */ }
            <Grid item xs={ 12 }>
              <TextField
                fullWidth
                required
                label={ "Enter title (max 100 characters)" }
                value={ newNotification.title }
                onChange={ ( e ) =>
                  setNewNotification( { ...newNotification, title: e.target.value } )
                }
                variant="outlined"
                placeholder="Enter title"
                inputProps={ {
                  maxLength: 100,     // 🔥 Limit title to 100 characters
                } }
                sx={ {
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: 'primary.main',
                  },
                } }
              />
            </Grid>

            {/* Message Field */ }
            <Grid item xs={ 12 }>
              <TextField
                fullWidth
                required
                label={ "Enter message (max 1024 characters)" }
                variant="outlined"
                placeholder="Enter message"
                value={ newNotification.message }
                onChange={ ( e ) =>
                  setNewNotification( { ...newNotification, message: e.target.value } )
                }
                multiline
                rows={ 4 }
                inputProps={ {
                  maxLength: 1024,     // 🔥 Limit message to 500 characters
                } }
              />
            </Grid>
          </Grid>

        </DialogContent>
        <DialogActions sx={ { p: 3, pt: 1, gap: 2 } }>
          <Button
            onClick={ handleCloseCreateDialog }
            variant="outlined"
            disabled={ loading }
            sx={ {
              minWidth: 120,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            } }
          >
            { t( 'notifications.cancel' ) }
          </Button>
          <Button
            onClick={ handleCreateNotification }
            variant="contained"
            disabled={ loading || !newNotification.title || !newNotification.message }
            startIcon={ loading ? <CircularProgress size={ 20 } /> : <AddIcon /> }
            sx={ {
              minWidth: 120,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              },
              '&:disabled': {
                background: 'grey.400',
                color: 'white.900'
              }
            } }
          >
            save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */ }
      <Dialog
        open={ deleteDialogOpen }
        onClose={ () => setDeleteDialogOpen( false ) }
        maxWidth="sm"
        fullWidth
      // PaperProps={{
      //   sx: {
      //     borderRadius: 3,
      //     overflow: 'hidden',
      //     maxHeight: '90vh'
      //   }
      // }}
      >
        <DialogTitle sx={ {
          bgcolor: 'error.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 2.5,
          px: 3
        } }>
          <DeleteIcon />
          Delete Notification
        </DialogTitle>
        <DialogContent style={ { paddingTop: "20px" } }>
          <Typography variant="body1">
            Are you sure you want to delete this notification?
          </Typography>
        </DialogContent>
        <DialogActions sx={ { p: 3, gap: 2 } } >
          <Button
            onClick={ () => setDeleteDialogOpen( false ) }
            variant="outlined"
            sx={ {
              minWidth: 100,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            } }
          >
            Cancel
          </Button>
          <Button
            onClick={ () => handleDeleteNotification( selectedNotification?.id?.toString() || '' ) }
            color="error"
            variant="contained"
            disabled={ loading }
            sx={ {
              minWidth: 120,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            } }
          >
            { loading ? 'Deleting...' : 'Delete' }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resend Confirmation Dialog */ }
      <Dialog
        open={ resendDialogOpen }
        onClose={ () => setResendDialogOpen( false ) }
        maxWidth="sm"
        fullWidth
      // PaperProps={{
      //   sx: {
      //     borderRadius: 3,
      //     overflow: 'hidden',
      //     maxHeight: '90vh'
      //   }
      // }}
      >
        <DialogTitle sx={ {
          bgcolor: 'info.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 2.5,
          px: 3
        } }>
          <RefreshIcon />
          Resend Notification
        </DialogTitle>
        <DialogContent sx={ { p: 3 } } style={ { paddingTop: "20px" } }>
          <Stack spacing={ 2 }>
            <Typography variant="body1">
              Are you sure you want to resend this notification?
            </Typography>
            { selectedNotification && (
              <Card variant="outlined" sx={ { p: 2 } }>
                <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                  { selectedNotification.title }
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  { selectedNotification.message }
                </Typography>
              </Card>
            ) }
            <Alert severity="info">
              This will send the notification to all users again.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={ { p: 3, gap: 2 } }>
          <Button
            onClick={ () => setResendDialogOpen( false ) }
            variant="outlined"
            sx={ {
              minWidth: 100,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            } }
          >
            Cancel
          </Button>
          <Button
            onClick={ () => handleResendNotification( selectedNotification?.id?.toString() || '' ) }
            color="primary"
            variant="contained"
            disabled={ loading }
            sx={ {
              minWidth: 120,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            } }
          >
            { loading ? 'Resending...' : 'Resend' }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Notifications */ }
      <Snackbar
        open={ toast.open }
        autoHideDuration={ 6000 }
        onClose={ handleCloseToast }
        anchorOrigin={ { vertical: 'top', horizontal: 'right' } }
        TransitionComponent={ Slide }
      >
        <Alert
          onClose={ handleCloseToast }
          severity={ toast.severity }
          sx={ {
            width: '100%',
            '& .MuiAlert-icon': { alignItems: 'center' }
          } }
          icon={ toast.severity === 'success' ? <CheckCircleIcon /> : undefined }
        >
          { toast.message }
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default NotificationList;
interface ExpandableTextProps {
  text: string;
  limit?: number;
}

const ExpandableText: React.FC<ExpandableTextProps> = ( { text, limit = 80 } ) => {
  const [ expanded, setExpanded ] = useState( false );

  if ( text.length <= limit ) return <>{ text }</>;

  return (
    <>
      { expanded ? text : text.substring( 0, limit ) + "... " }
      <span
        style={ { color: "#1976d2", cursor: "pointer", fontWeight: 600 } }
        onClick={ () => setExpanded( !expanded ) }
      >
        { expanded ? "Read Less" : "Read More" }
      </span>
    </>
  );
};
